import { findZeros } from "@/lib/tool-kit/calculus/plot";

/**
 * Average rate of change (the slope of the secant line) of a function or a table of
 * measurements, and how it approaches the instantaneous rate as the interval shrinks.
 *
 *     average rate over [a, b] = (f(b) − f(a)) / (b − a)
 */

export type Curve = (x: number) => number;

/** (f(b) − f(a))/(b − a); NaN when a = b or f is undefined at an end. */
export function averageRate(
  fa: number,
  fb: number,
  a: number,
  b: number
): number {
  return b === a ? Number.NaN : (fb - fa) / (b - a);
}

export interface ApproachRow {
  h: number;
  /** The average rate over [a, a + h]. */
  rate: number;
}

/** Average rates over [a, a + h] for shrinking h, which tend to f′(a). */
export function approachTable(
  f: Curve,
  a: number,
  steps: number[] = [1, 0.1, 0.01, 0.001, 0.0001]
): ApproachRow[] {
  const fa = f(a);
  return steps.map((h) => ({ h, rate: averageRate(fa, f(a + h), a, a + h) }));
}

/**
 * Points c in (a, b) where f′(c) equals the average rate (the Mean Value Theorem says at
 * least one exists for a function that is continuous on [a, b] and differentiable inside).
 */
export function meanValuePoints(
  derivative: Curve,
  rate: number,
  a: number,
  b: number
): number[] {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  return findZeros((x) => derivative(x) - rate, lo, hi).filter(
    (c) => c > lo && c < hi
  );
}

export interface DataPoint {
  x: number;
  y: number;
}

export interface TableRate {
  from: DataPoint;
  to: DataPoint;
  rate: number;
}

/** Rates between consecutive points after sorting by x (equal x values are skipped). */
export function consecutiveRates(points: DataPoint[]): TableRate[] {
  const sorted = [...points].sort((p, q) => p.x - q.x);
  const rates: TableRate[] = [];
  for (let i = 1; i < sorted.length; i += 1) {
    const [from, to] = [sorted[i - 1], sorted[i]];
    if (to.x !== from.x) {
      rates.push({ from, to, rate: averageRate(from.y, to.y, from.x, to.x) });
    }
  }
  return rates;
}

/** The data point whose x matches (within rounding), or null. */
export function pointAt(points: DataPoint[], x: number): DataPoint | null {
  return (
    points.find((p) => Math.abs(p.x - x) <= 1e-12 * Math.max(1, Math.abs(x))) ??
    null
  );
}
